import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  NgZone,
  OnDestroy
} from '@angular/core';
import { DragonFlight, DragonPose } from './dragon-flight';

/**
 * The landing hero's illustration: a writer's desk under an arched window at dusk.
 * Ambient motion only (stars, steam, a passing dragon, the lamp, a moth, a page writing itself).
 * It pauses while the hero is off-screen and is left out for visitors who prefer
 * reduced motion: CSS handles the stylesheet animations, `animate` the SVG one and the dragon.
 */
@Component({
  selector: 'app-hero-window',
  templateUrl: './hero-window.component.html',
  styleUrls: ['./hero-window.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HeroWindowComponent implements AfterViewInit, OnDestroy {

  readonly animate = typeof window === 'undefined'
    || !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /** The still pose in the markup; once flying, the dragon's paths are set directly each frame. */
  readonly dragon = DragonFlight.restingPose();

  private observer?: IntersectionObserver;
  private frame = 0;
  private stopped = false;

  constructor(private host: ElementRef<HTMLElement>, private zone: NgZone) {
  }

  ngAfterViewInit() {
    if (typeof IntersectionObserver === 'undefined') {
      return;
    }

    const root = this.host.nativeElement;
    const svg = root.querySelector('svg');
    const fly = this.animate ? this.dragonFlier(root) : undefined;
    this.zone.runOutsideAngular(() => {
      this.observer = new IntersectionObserver(([entry]) => {
        root.classList.toggle('is-paused', !entry.isIntersecting);
        if (entry.isIntersecting) {
          svg?.unpauseAnimations();
          fly?.(true);
        } else {
          svg?.pauseAnimations();
          fly?.(false);
        }
      });
      this.observer.observe(root);
    });
  }

  ngOnDestroy() {
    this.observer?.disconnect();
    this.stopped = true;
    if (this.frame) {
      cancelAnimationFrame(this.frame);
    }
  }

  /** Returns a switch that runs or pauses the dragon's flight; time stands still while paused. */
  private dragonFlier(root: HTMLElement): ((running: boolean) => void) | undefined {
    const group = root.querySelector<SVGGElement>('.tsw-dragon');
    const [farWing, body, nearWing] = Array.from(group?.querySelectorAll('path') ?? []);
    if (!group || !farWing || !body || !nearWing) {
      return undefined;
    }

    const flight = new DragonFlight();
    let last: number | undefined;
    let running = false;

    const show = (pose: DragonPose | null) => {
      group.classList.toggle('is-flying', pose !== null);
      if (pose) {
        group.setAttribute('transform', pose.transform);
        farWing.setAttribute('d', pose.farWing);
        body.setAttribute('d', pose.body);
        nearWing.setAttribute('d', pose.nearWing);
      }
    };

    const tick = (now: number) => {
      // Cap the step so a dropped frame or a background tab doesn't make it jump.
      const dt = last === undefined ? 0 : Math.min(.05, (now - last) / 1000);
      last = now;
      show(flight.advance(dt));
      if (running && !this.stopped) {
        this.frame = requestAnimationFrame(tick);
      }
    };

    return next => {
      if (next === running) {
        return;
      }
      running = next;
      cancelAnimationFrame(this.frame);
      if (running && !this.stopped) {
        last = undefined;
        this.frame = requestAnimationFrame(tick);
      }
    };
  }
}
